/* eslint-disable react/prop-types */
import { CreditCard, Radio, Receipt, User, DollarSign, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import useSettings from "@/hooks/useSettings";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * OrderCardPaymentConfirmDialog displays a modern confirmation modal before dispatching
 * payment. Conditioned by settings.has_pax_terminal (Terminal vs Manual Card payment).
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {() => void} props.onConfirm
 * @param {boolean} props.isLoading
 * @param {object} props.orderData - Active order data
 * @param {number|string} [props.paymentAmount] - Specific amount being charged (e.g. advance amount)
 */
export default function OrderCardPaymentConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  orderData,
  paymentAmount,
}) {
  const { settings } = useSettings();
  const hasPaxTerminal = settings?.has_pax_terminal === true;

  const displayAmount = Number(
    paymentAmount !== undefined && paymentAmount !== null
      ? paymentAmount
      : orderData?.total_price || orderData?.order_total || 0
  ).toFixed(2);
  const customerName = orderData?.customer?.full_name || orderData?.customer_full_name || orderData?.customer_name || "Walk-in Customer";
  const orderType = orderData?.order_type || "pickup";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[440px] bg-cblack4 border-gray-800 text-white p-6 shadow-2xl animate-fadeIn">
        <DialogHeader className="flex flex-col justify-center items-center gap-3 text-center">
          {/* Animated Cyan Glow Card Icon */}
          <div className="relative flex justify-center items-center h-13 w-13 rounded-2xl bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 p-3 shadow-inner">
            <CreditCard className="h-7 w-7" />
            {hasPaxTerminal && (
              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-cyan-500" />
              </span>
            )}
          </div>

          <DialogTitle className="text-white text-lg font-bold tracking-wide">
            {hasPaxTerminal ? "Pay with Card Terminal?" : "Confirm Card Payment?"}
          </DialogTitle>

          <DialogDescription className="text-cwhite3 text-xs leading-relaxed max-w-[320px]">
            {hasPaxTerminal ? (
              <>
                This will send a sale transaction request of <strong className="text-white">£{displayAmount}</strong> to the connected card reader.
              </>
            ) : (
              <>
                Mark this order as paid by Card for <strong className="text-white">£{displayAmount}</strong>.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Order Details Preview Box */}
        <div className="mt-2 space-y-3">
          {/* Amount Display */}
          <div className="bg-cblack3 p-3.5 rounded-xl border border-gray-800 text-center flex flex-col items-center justify-center">
            <span className="text-[10px] text-cwhite3 uppercase tracking-wider font-semibold">
              Charge Amount
            </span>
            <span className="text-cyellow text-2xl font-extrabold font-sans mt-0.5">
              £{displayAmount}
            </span>
          </div>

          {/* Quick Meta Row */}
          <div className="bg-cblack3 p-3 rounded-xl border border-gray-800 space-y-2 text-xs">
            <div className="flex justify-between items-center pb-2 border-b border-gray-800/60">
              <span className="text-cwhite3 flex items-center gap-1.5">
                <Receipt className="h-3.5 w-3.5 text-cyan-400" />
                Order Reference
              </span>
              <span className="font-semibold text-white font-mono">
                Order #{orderData?.id}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-cwhite3 flex items-center gap-1.5">
                <User className="h-3.5 w-3.5 text-cyellow" />
                Customer
              </span>
              <span className="font-medium text-white truncate max-w-[180px]">
                {customerName}
              </span>
            </div>

            <div className="flex justify-between items-center pt-1 border-t border-gray-800/40 text-[11px]">
              <span className="text-cwhite3">Order Channel</span>
              <span className="font-semibold text-cwhite2 uppercase">
                {orderType}
              </span>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <div className="grid grid-cols-2 gap-3 w-full pt-1">
            <Button
              type="button"
              variant="outline"
              className="bg-cblack3 hover:bg-cblack7 border-gray-800 text-cwhite3 hover:text-white font-medium py-2 rounded-lg transition"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              isLoading={isLoading}
              className="bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-2 rounded-lg shadow-md transition"
              onClick={() => {
                onConfirm?.();
              }}
            >
              {hasPaxTerminal ? "Dispatch Payment" : "Confirm Card Payment"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
